import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { clinicalAnalyticsActions } from 'store/clinical';
import { insightsTabEnums } from './constants';
import { OverviewTab } from './overviewTab';
import { DetailedInsightsTab } from './detailedInsightsTab';

const DEFAULT_DRAWER_WIDTH = 640;

export const ClinicalInsightsDrawer = ({ open, onClose, clientId, clientName }) => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState(insightsTabEnums.OVERVIEW);
    const [drawerWidth, setDrawerWidth] = useState(DEFAULT_DRAWER_WIDTH);
    const [isResizing, setIsResizing] = useState(false);

    useEffect(() => {
        if (open && clientId) {
            dispatch(clinicalAnalyticsActions.getClientOverview(clientId));
        }
        return () => {
            if (!open) {
                dispatch(clinicalAnalyticsActions.resetClinicalAnalytics());
            }
        };
    }, [open, clientId]);

    useEffect(() => {
        if (!open) {
            setIsResizing(false);
            setDrawerWidth(DEFAULT_DRAWER_WIDTH);
        }
    }, [open]);

    useEffect(() => {
        if (!isResizing) return undefined;

        const handleMouseMove = (event) => {
            const viewportWidth = window.innerWidth || DEFAULT_DRAWER_WIDTH;
            const minWidth = Math.min(DEFAULT_DRAWER_WIDTH, viewportWidth);
            const maxWidth = Math.max(minWidth, Math.floor(viewportWidth * 0.7));
            const nextWidth = viewportWidth - event.clientX;

            setDrawerWidth(Math.min(Math.max(nextWidth, minWidth), maxWidth));
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        document.body.style.userSelect = 'none';
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.body.style.userSelect = '';
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    return (
        <Drawer
            sx={{ zIndex: 1300 }}
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', md: drawerWidth },
                    maxWidth: '100vw',
                },
            }}
        >
            <div className="clinical-insights-drawer">
                <button
                    type="button"
                    className={`clinical-insights-resize-handle ${isResizing ? 'active' : ''}`}
                    onMouseDown={() => setIsResizing(true)}
                    aria-label="Resize insights drawer"
                />
                <div className="clinical-insights-header">
                    <div>
                        <h2 className="clinical-insights-title">Client Clinical Insights</h2>
                        {clientName && <p className="clinical-insights-subtitle">{clientName}</p>}
                    </div>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </div>

                <div className="clinical-insights-tabs">
                    <button
                        className={`clinical-insights-tab ${activeTab === insightsTabEnums.OVERVIEW ? 'active' : ''}`}
                        onClick={() => setActiveTab(insightsTabEnums.OVERVIEW)}
                    >
                        Overview
                    </button>
                    <button
                        className={`clinical-insights-tab ${activeTab === insightsTabEnums.DETAILED ? 'active' : ''}`}
                        onClick={() => setActiveTab(insightsTabEnums.DETAILED)}
                    >
                        Detailed/Progress Insights
                    </button>
                </div>

                <div className="clinical-insights-content">
                    {activeTab === insightsTabEnums.OVERVIEW && <OverviewTab clientId={clientId} />}
                    {activeTab === insightsTabEnums.DETAILED && <DetailedInsightsTab clientId={clientId} clientName={clientName} />}
                </div>
            </div>
        </Drawer>
    );
};
